import React, { useEffect, useState, useRef } from "react";
import Calling from '../../../api/Calling';
import { toast } from "react-toastify";
import * as echarts from 'echarts';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import { Skeleton, Button, TextField, InputAdornment, Select, MenuItem, FormControl } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
    CalendarDays,
    CalendarRange,
    TrendingUp,
    Activity,
    Eye,
    MousePointerClick,
    Award,
    Search,
    Filter,
    AlertCircle,
    CheckCircle2,
    XCircle,
    Download,
    X,
    RefreshCw,
    ArrowLeft,
    ArrowRight,
    ChevronDown,
    Check,
} from "lucide-react";
import TableSkeleton from "../../../components/ul/TableSkeleton";
import getISTDate from "../../../components/getISTDate";

// Brand palette
const BRAND = '#34C759';
const BRAND_HOVER = '#2db84d';
const BRAND_SOFT = 'rgba(52,199,89,0.10)';
const BRAND_RING = 'rgba(52,199,89,0.45)';

// Premium green-accent outlined field styling for the date pickers
const dateFieldSx = {
    minWidth: 168,
    '& .MuiInputLabel-root': { fontSize: '0.8rem', fontWeight: 500, color: '#6b7280' },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#111827',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_RING },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${BRAND_SOFT}` },
    },
    // Brand the open-picker (calendar) icon button
    '& .MuiInputAdornment-root .MuiIconButton-root': {
        color: '#9ca3af',
        borderRadius: '8px',
        transition: 'color .15s ease, background-color .15s ease',
        '&:hover': { color: BRAND, backgroundColor: BRAND_SOFT },
    },
};
const applyBtnSx = {
    backgroundColor: BRAND,
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: '0 6px 16px -6px rgba(52,199,89,0.6)',
    whiteSpace: 'nowrap',
    px: 2.75,
    height: 40,
    '&:hover': { backgroundColor: BRAND_HOVER, boxShadow: '0 8px 20px -6px rgba(52,199,89,0.7)' },
};

// Premium, fully-branded theme for the calendar popup.
// All component keys & style slots below are verified to exist in @mui/x-date-pickers@9.6.0.
const pickerTheme = createTheme({
    palette: { primary: { main: BRAND } },
    shape: { borderRadius: 12 },
    typography: { fontWeightMedium: 600 },
    components: {
        // Popup surface (paper) — rounded + soft premium shadow
        MuiPickerPopper: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    marginTop: 6,
                    border: '1px solid #eef0f2',
                    boxShadow: '0 18px 50px -12px rgba(16,24,40,0.22), 0 8px 16px -8px rgba(16,24,40,0.10)',
                    overflow: 'hidden',
                },
            },
        },
        // Calendar header: month/year label + nav area
        MuiPickersCalendarHeader: {
            styleOverrides: {
                root: { paddingLeft: 18, paddingRight: 14, marginTop: 12, marginBottom: 4 },
                label: { fontWeight: 700, fontSize: '0.95rem', color: '#111827' },
                switchViewButton: {
                    color: '#374151',
                    '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND },
                },
            },
        },
        // Nav arrows (prev / next month)
        MuiPickersArrowSwitcher: {
            styleOverrides: {
                button: {
                    color: '#6b7280',
                    borderRadius: 10,
                    '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND },
                },
            },
        },
        // Weekday labels (Mo, Tu, …)
        MuiDayCalendar: {
            styleOverrides: {
                weekDayLabel: {
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    color: '#9ca3af',
                    textTransform: 'uppercase',
                },
            },
        },
        // Individual day cells
        MuiPickerDay: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    borderRadius: 10,
                    transition: 'background-color .12s ease, color .12s ease',
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&:focus': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': {
                        backgroundColor: BRAND,
                        color: '#fff',
                        boxShadow: '0 4px 10px -3px rgba(52,199,89,0.6)',
                        '&:hover': { backgroundColor: BRAND_HOVER },
                        '&:focus': { backgroundColor: BRAND_HOVER },
                    },
                },
                today: {
                    border: `1.5px solid ${BRAND}`,
                    color: BRAND,
                    fontWeight: 700,
                    '&:not(.Mui-selected)': { backgroundColor: BRAND_SOFT },
                },
            },
        },
        // Year selection grid
        MuiYearCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': {
                        backgroundColor: BRAND,
                        color: '#fff',
                        '&:hover': { backgroundColor: BRAND_HOVER },
                        '&:focus': { backgroundColor: BRAND_HOVER },
                    },
                },
            },
        },
        // Month selection grid
        MuiMonthCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': {
                        backgroundColor: BRAND,
                        color: '#fff',
                        '&:hover': { backgroundColor: BRAND_HOVER },
                        '&:focus': { backgroundColor: BRAND_HOVER },
                    },
                },
            },
        },
    },
});

// Shared slots for both pickers — branded lucide icons (verified slot names for v9.6)
const pickerSlots = {
    openPickerIcon: () => <CalendarDays size={18} />,
    leftArrowIcon: () => <ArrowLeft size={18} />,
    rightArrowIcon: () => <ArrowRight size={18} />,
    switchViewIcon: () => <ChevronDown size={18} />,
};

const QUICK_RANGES = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'thisWeek', label: 'This Week' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'last6Months', label: 'Last 6 Months' },
    { key: 'thisYear', label: 'This Year' },
];

// Compact green-accent select styling used inside modals
const modalSelectSx = {
    borderRadius: '12px',
    backgroundColor: '#fff',
    fontSize: '0.875rem',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_RING },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND, borderWidth: '1.5px' },
};

// Helper function to format time only (using UTC)
const formatTimeOnly = (dateString) => {
    const date = new Date(dateString);
    let hours = date.getUTCHours();
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes}:${seconds} ${ampm}`;
};

// Helper function to format date only (DD-MM-YYYY) using UTC
const formatDateOnly = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}-${month}-${year}`;
};

// Helper function to format date for display (DD MMM YYYY) using UTC
const formatDateDisplay = (dateString) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = months[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    return `${day} ${month} ${year}`;
};

// Helper function to get UTC timestamp for sorting
const getUTCTimestamp = (dateString) => {
    return new Date(dateString).getTime();
};

// EventDetailModal Component
const EventDetailModal = ({ eventType, eventData, allAdsData, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('count');
    const [sortOrder, setSortOrder] = useState('desc');
    const [expandedTimelines, setExpandedTimelines] = useState({});

    const getEventColor = (eventType) => {
        const colors = {
            onAdClicked: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', gradient: 'from-yellow-500 to-yellow-600' },
            onAdLoaded: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', gradient: 'from-blue-500 to-blue-600' },
            onAdFailedToLoad: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', gradient: 'from-red-500 to-red-600' },
            onAdImpression: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', gradient: 'from-purple-500 to-purple-600' },
            onAdShowedFullScreenContent: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', gradient: 'from-green-500 to-green-600' },
            onAdDismissedFullScreenContent: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', gradient: 'from-orange-500 to-orange-600' },
            onAdFailedToShowFullScreenContent: { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300', gradient: 'from-pink-500 to-pink-600' },
            onAdUnitNotFound: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300', gradient: 'from-gray-500 to-gray-600' },
            onRequestSend: { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-300', gradient: 'from-cyan-500 to-cyan-600' },
        };
        return colors[eventType] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300', gradient: 'from-gray-500 to-gray-600' };
    };

    const eventColor = getEventColor(eventType);

    const getGroupedEventData = () => {
        const grouped = {};
        const adConfigMap = {};
        Object.values(allAdsData).flat().forEach(ad => {
            if (ad._id) {
                adConfigMap[ad._id] = ad;
            }
        });

        eventData
            .filter(event => event.a_event === eventType)
            .forEach(event => {
                const key = event.record_id;
                if (!grouped[key]) {
                    const adConfig = adConfigMap[event.record_id];
                    if (adConfig) {
                        grouped[key] = {
                            id: key,
                            record_id: event.record_id,
                            a_id: adConfig.a_id,
                            a_name: adConfig.a_name || 'Unknown Ad',
                            a_description: adConfig.a_description || '',
                            a_group: adConfig.a_group || 'N/A',
                            a_group_title: adConfig.a_group_title || 'N/A',
                            a_img: adConfig.a_img || '',
                            totalCount: 0,
                            events: []
                        };
                    } else {
                        grouped[key] = {
                            id: key,
                            record_id: event.record_id,
                            a_id: event.a_id || 'Unknown',
                            a_name: event.a_name || 'Unknown Ad',
                            a_description: event.a_description || '',
                            a_group: event.a_group || 'N/A',
                            a_group_title: event.a_group_title || 'N/A',
                            a_img: event.a_img || '',
                            totalCount: 0,
                            events: []
                        };
                    }
                }
                grouped[key].totalCount += event.a_count || 0;
                grouped[key].events.push(event);
            });

        return Object.values(grouped).filter(item => item.events.length > 0);
    };

    const groupedData = getGroupedEventData();

    const filteredData = groupedData.filter(item => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            item.a_name?.toLowerCase().includes(searchLower) ||
            item.a_id?.toLowerCase().includes(searchLower) ||
            item.a_description?.toLowerCase().includes(searchLower) ||
            item.a_group_title?.toLowerCase().includes(searchLower) ||
            item.record_id?.toLowerCase().includes(searchLower)
        );
    });

    const sortedData = [...filteredData].sort((a, b) => {
        let comparison = 0;
        switch (sortBy) {
            case 'count':
                comparison = a.totalCount - b.totalCount;
                break;
            case 'date':
                const aLatest = Math.max(...a.events.map(e => getUTCTimestamp(e.createdAt)));
                const bLatest = Math.max(...b.events.map(e => getUTCTimestamp(e.createdAt)));
                comparison = aLatest - bLatest;
                break;
            case 'name':
                comparison = (a.a_name || '').localeCompare(b.a_name || '');
                break;
            default:
                comparison = 0;
        }
        return sortOrder === 'asc' ? comparison : -comparison;
    });

    const totalEvents = sortedData.reduce((sum, item) => sum + item.totalCount, 0);

    const toggleTimeline = (itemId) => {
        setExpandedTimelines(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    const groupEventsByDate = (events) => {
        const grouped = {};
        events.forEach(event => {
            const dateKey = formatDateDisplay(event.createdAt);
            if (!grouped[dateKey]) {
                grouped[dateKey] = {
                    date: dateKey,
                    timestamp: getUTCTimestamp(event.createdAt),
                    events: []
                };
            }
            grouped[dateKey].events.push(event);
        });
        return Object.values(grouped).sort((a, b) => b.timestamp - a.timestamp);
    };

    return (
        <motion.div
            key="ads-event-detail-modal"
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
        >
            <motion.div
                className="relative bg-main-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden border border-main-border"
                initial={{ scale: 0.95, y: 14, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.95, y: 14, opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={`flex items-center justify-between p-6 border-b border-main-border bg-gradient-to-r ${eventColor.gradient}`}>
                    <div>
                        <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                            <Activity size={26} />
                            {eventType} Analytics
                        </h3>
                        <p className="text-white text-opacity-90 mt-2">
                            {sortedData.length} configurations • {totalEvents.toLocaleString()} total events
                        </p>
                    </div>
                    <button onClick={onClose} className="w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-colors backdrop-blur-sm">
                        <X size={20} className="text-white" />
                    </button>
                </div>

                <div className="p-6 border-b border-main-border bg-main-bg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-main-font mb-2">Search</label>
                            <TextField
                                fullWidth
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search ad name, ID, description..."
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' } }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search size={18} className="text-main-subfont" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-main-font mb-2">Sort By</label>
                            <FormControl fullWidth size="small">
                                <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} sx={modalSelectSx}>
                                    <MenuItem value="count">Event Count</MenuItem>
                                    <MenuItem value="date">Latest Event</MenuItem>
                                    <MenuItem value="name">Ad Name</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-main-font mb-2">Sort Order</label>
                            <FormControl fullWidth size="small">
                                <Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} sx={modalSelectSx}>
                                    <MenuItem value="desc">Highest First</MenuItem>
                                    <MenuItem value="asc">Lowest First</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                    </div>
                </div>

                <div className="p-6 overflow-auto max-h-[calc(90vh-280px)]">
                    {sortedData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-main-subfont">
                            <div className="w-20 h-20 bg-main-bg rounded-full flex items-center justify-center mb-4">
                                <AlertCircle size={36} />
                            </div>
                            <p className="text-lg font-semibold text-main-font">No records found</p>
                            <p className="text-sm">Try adjusting your search</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {sortedData.map((item) => {
                                const groupedByDate = groupEventsByDate(item.events);
                                const isExpanded = expandedTimelines[item.id];
                                const visibleDates = isExpanded ? groupedByDate : groupedByDate.slice(0, 1);
                                const hasMoreDates = groupedByDate.length > 1;

                                return (
                                    <div key={item.id} className={`${eventColor.bg} border-2 ${eventColor.border} rounded-2xl p-5 hover:shadow-lg transition-all`}>
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <h4 className="font-bold text-main-font text-lg mb-1">{item.a_name}</h4>
                                                {item.a_description && (
                                                    <p className="text-sm text-main-subfont mb-2">
                                                        {item.a_description.length > 80 ? `${item.a_description.substring(0, 80)}...` : item.a_description}
                                                    </p>
                                                )}
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    <span className="px-2 py-1 text-xs font-semibold bg-white rounded-full">Group: {item.a_group}</span>
                                                    <span className="px-2 py-1 text-xs font-semibold bg-white rounded-full">{item.a_group_title}</span>
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <div className="w-16 h-16 bg-white rounded-xl flex flex-col items-center justify-center shadow-md">
                                                    <span className="text-2xl font-bold text-main-font">{item.totalCount}</span>
                                                    <span className="text-xs text-main-subfont">events</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-3 p-2 bg-white bg-opacity-50 rounded-lg">
                                            <span className="text-xs font-medium text-main-subfont">Ad ID:</span>
                                            <p className="text-xs font-mono text-main-font truncate mt-1" title={item.a_id}>{item.a_id}</p>
                                        </div>

                                        <div className="mb-3 p-2 bg-white bg-opacity-50 rounded-lg">
                                            <span className="text-xs font-medium text-main-subfont">Record ID:</span>
                                            <p className="text-xs font-mono text-main-font truncate mt-1" title={item.record_id}>{item.record_id}</p>
                                        </div>

                                        <div className="border-t border-gray-300 pt-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <h5 className="text-xs font-semibold text-main-font flex items-center gap-1">
                                                    <CalendarDays size={13} />
                                                    Event Timeline ({item.events.length} occurrences)
                                                </h5>
                                                {hasMoreDates && <span className="text-xs text-main-subfont">{groupedByDate.length} dates</span>}
                                            </div>
                                            <div className="space-y-2">
                                                {visibleDates.map((dateGroup, dateIdx) => {
                                                    const dateTotal = dateGroup.events.reduce((sum, e) => sum + (e.a_count || 0), 0);
                                                    return (
                                                        <div key={dateIdx} className="bg-white bg-opacity-70 rounded-lg p-3">
                                                            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
                                                                <div className="flex items-center gap-2">
                                                                    <CalendarDays size={14} className="text-main-subfont" />
                                                                    <span className="text-sm font-bold text-main-font">{dateGroup.date}</span>
                                                                </div>
                                                                <span className="text-sm font-bold text-main-font bg-white px-2 py-1 rounded">{dateTotal} events</span>
                                                            </div>
                                                            <div className="space-y-1.5 max-h-24 overflow-y-auto">
                                                                {dateGroup.events
                                                                    .sort((a, b) => getUTCTimestamp(b.createdAt) - getUTCTimestamp(a.createdAt))
                                                                    .map((event, eventIdx) => (
                                                                        <div key={eventIdx} className="flex items-center justify-between text-xs bg-main-bg p-2 rounded">
                                                                            <span className="text-main-subfont">{formatTimeOnly(event.createdAt)}</span>
                                                                            <span className="font-bold text-main-font">{event.a_count}</span>
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {hasMoreDates && (
                                                    <button onClick={() => toggleTimeline(item.id)} className="w-full mt-2 px-3 py-2 bg-white hover:bg-main-bg border-2 border-gray-300 rounded-lg text-xs font-semibold text-main-font transition-all flex items-center justify-center gap-2">
                                                        {isExpanded ? (
                                                            <><ChevronDown size={14} className="rotate-180" /> Show Less ({groupedByDate.length - 1} hidden)</>
                                                        ) : (
                                                            <><ChevronDown size={14} /> Show {groupedByDate.length - 1} More Date{groupedByDate.length - 1 !== 1 ? 's' : ''}</>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

const ImagePreviewModal = ({ imageUrl, onClose, adName }) => {
    return (
        <motion.div
            key="ads-image-preview-modal"
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
        >
            <motion.div
                className="relative bg-main-white rounded-2xl shadow-2xl max-w-4xl max-h-[90vh] overflow-hidden border border-main-border"
                initial={{ scale: 0.95, y: 14, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.95, y: 14, opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-4 border-b border-main-border bg-main-bg">
                    <h3 className="text-lg font-semibold text-main-font">{adName || 'Ad Image Preview'}</h3>
                    <button onClick={onClose} className="w-9 h-9 bg-main-red/10 hover:bg-main-red/20 rounded-xl flex items-center justify-center transition-colors">
                        <X size={20} className="text-main-red" />
                    </button>
                </div>
                <div className="p-4 overflow-auto max-h-[calc(90vh-80px)]">
                    <img
                        src={imageUrl}
                        alt={adName || 'Ad Image'}
                        className="w-60 h-auto rounded-lg"
                        onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3Ctext fill="%236b7280" font-family="Arial" font-size="20" text-anchor="middle" x="200" y="150"%3EImage not available%3C/text%3E%3C/svg%3E';
                        }}
                    />
                </div>
            </motion.div>
        </motion.div>
    );
};

const EventsDetailModal = ({ eventsByDate, adName, onClose }) => {
    const [selectedEventFilter, setSelectedEventFilter] = useState('all');

    const allEventTypes = new Set();
    Object.values(eventsByDate).forEach(events => {
        events.forEach(event => allEventTypes.add(event.a_event));
    });

    const getFilteredEvents = () => {
        if (selectedEventFilter === 'all') return eventsByDate;
        const filtered = {};
        Object.keys(eventsByDate).forEach(date => {
            const filteredDateEvents = eventsByDate[date].filter(event => event.a_event === selectedEventFilter);
            if (filteredDateEvents.length > 0) {
                filtered[date] = filteredDateEvents;
            }
        });
        return filtered;
    };

    const filteredEvents = getFilteredEvents();

    const eventTypeCounts = {};
    Object.values(eventsByDate).forEach(events => {
        events.forEach(event => {
            if (!eventTypeCounts[event.a_event]) {
                eventTypeCounts[event.a_event] = 0;
            }
            eventTypeCounts[event.a_event] += event.a_count || 0;
        });
    });

    const getEventColor = (eventType) => {
        const colors = {
            onAdClicked: 'bg-yellow-100 text-yellow-700 border-yellow-300',
            onAdLoaded: 'bg-blue-100 text-blue-700 border-blue-300',
            onAdFailedToLoad: 'bg-red-100 text-red-700 border-red-300',
            onAdImpression: 'bg-purple-100 text-purple-700 border-purple-300',
            onAdShowedFullScreenContent: 'bg-green-100 text-green-700 border-green-300',
            onAdDismissedFullScreenContent: 'bg-orange-100 text-orange-700 border-orange-300',
            onAdFailedToShowFullScreenContent: 'bg-pink-100 text-pink-700 border-pink-300',
            onAdUnitNotFound: 'bg-gray-100 text-gray-700 border-gray-300',
            onRequestSend: 'bg-cyan-100 text-cyan-700 border-cyan-300',
        };
        return colors[eventType] || 'bg-gray-100 text-gray-700 border-gray-300';
    };

    const sortDates = (dateKeys) => {
        return dateKeys.sort((a, b) => {
            const [dayA, monthA, yearA] = a.split('-').map(Number);
            const [dayB, monthB, yearB] = b.split('-').map(Number);
            const dateA = new Date(Date.UTC(yearA, monthA - 1, dayA));
            const dateB = new Date(Date.UTC(yearB, monthB - 1, dayB));
            return dateB - dateA;
        });
    };

    return (
        <motion.div
            key="ads-events-detail-modal"
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
        >
            <motion.div
                className="relative bg-main-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden border border-main-border"
                initial={{ scale: 0.95, y: 14, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.95, y: 14, opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-6 border-b border-main-border bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div>
                        <h3 className="text-xl font-bold text-main-font flex items-center gap-2">
                            <Activity size={22} className="text-main-exBlue" />
                            Event Details: {adName}
                        </h3>
                        <p className="text-sm text-main-subfont mt-1">
                            {Object.keys(eventsByDate).length} dates • {Object.values(eventsByDate).flat().length} total events
                        </p>
                    </div>
                    <button onClick={onClose} className="w-10 h-10 bg-main-red/10 hover:bg-main-red/20 rounded-xl flex items-center justify-center transition-colors">
                        <X size={20} className="text-main-red" />
                    </button>
                </div>

                <div className="p-4 border-b border-main-border bg-main-bg">
                    <div className="flex items-center gap-2 mb-3">
                        <Filter size={16} className="text-main-subfont" />
                        <span className="text-sm font-semibold text-main-font">Filter by Event Type:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setSelectedEventFilter('all')}
                            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${selectedEventFilter === 'all'
                                ? 'bg-main-blue text-main-white shadow-md'
                                : 'bg-main-white text-main-font border border-main-border hover:border-main-blue/50'
                                }`}
                        >
                            All Events ({Object.values(eventsByDate).flat().length})
                        </button>
                        {Array.from(allEventTypes).sort().map(eventType => (
                            <button
                                key={eventType}
                                onClick={() => setSelectedEventFilter(eventType)}
                                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${selectedEventFilter === eventType
                                    ? getEventColor(eventType) + ' shadow-md border'
                                    : 'bg-main-white text-main-font border border-main-border hover:border-gray-400'
                                    }`}
                            >
                                {eventType} ({eventTypeCounts[eventType]})
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-6 overflow-auto max-h-[calc(90vh-250px)]">
                    {Object.keys(filteredEvents).length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-main-subfont">
                            <div className="w-20 h-20 bg-main-bg rounded-full flex items-center justify-center mb-4">
                                <AlertCircle size={36} />
                            </div>
                            <p className="text-lg font-semibold text-main-font">No events found</p>
                            <p className="text-sm">Try selecting a different event type</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {sortDates(Object.keys(filteredEvents)).map((date, dateIdx) => (
                                <div key={dateIdx} className="bg-main-white border-2 border-main-border rounded-2xl p-4 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-main-border">
                                        <div className="w-12 h-12 bg-main-exBlue/10 rounded-xl flex items-center justify-center">
                                            <CalendarDays size={22} className="text-main-exBlue" />
                                        </div>
                                        <div>
                                            <h4 className="text-lg font-bold text-main-font">{date}</h4>
                                            <p className="text-sm text-main-subfont">
                                                {filteredEvents[date].length} event{filteredEvents[date].length !== 1 ? 's' : ''} •{' '}
                                                Total count: {filteredEvents[date].reduce((sum, e) => sum + (e.a_count || 0), 0)}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {filteredEvents[date].map((event, idx) => (
                                            <div key={idx} className={`${getEventColor(event.a_event)} border-2 rounded-xl p-4 hover:shadow-md transition-all`}>
                                                <div className="flex items-start justify-between mb-2">
                                                    <span className="text-xs font-bold uppercase tracking-wide">{event.a_event}</span>
                                                    <span className="text-lg font-bold">{event.a_count}</span>
                                                </div>
                                                <div className="text-xs opacity-75 space-y-1">
                                                    <div className="flex items-center gap-1">
                                                        <CalendarDays size={12} />
                                                        <span>{formatTimeOnly(event.createdAt)}</span>
                                                    </div>
                                                    {event.a_id && (
                                                        <div className="text-xs font-mono truncate" title={event.a_id}>
                                                            ID: {event.a_id.substring(0, 20)}...
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

const AdsTrack = () => {
    const [adsData, setAdsData] = useState([]);
    const [allAdsData, setAllAdsData] = useState({});
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    const [loading, setLoading] = useState(false);
    const [selectedBtn, setSelectedBtn] = useState('today');
    const [selectedType, setSelectedType] = useState('all');
    const [selectedAdId, setSelectedAdId] = useState('all');
    const [selectedGroup, setSelectedGroup] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [previewAdName, setPreviewAdName] = useState('');
    const [showEventsModal, setShowEventsModal] = useState(false);
    const [selectedEventsByDate, setSelectedEventsByDate] = useState({});
    const [selectedAdName, setSelectedAdName] = useState('');
    const [showEventDetailModal, setShowEventDetailModal] = useState(false);
    const [selectedEventType, setSelectedEventType] = useState('');

    // Draft (uncommitted) date-range values for the pickers — applied only on "Apply" click
    const [draftStart, setDraftStart] = useState(dayjs(getISTDate()));
    const [draftEnd, setDraftEnd] = useState(dayjs(getISTDate()));

    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    const [summary, setSummary] = useState({
        bannerAds: { total: 0, totalCount: 0 },
        fullScreenAds: { total: 0, totalCount: 0 },
        nativeAds: { total: 0, totalCount: 0 },
        openAds: { total: 0, totalCount: 0 },
        rewardAds: { total: 0, totalCount: 0 },
        rewardFsAds: { total: 0, totalCount: 0 },
    });

    const fetchAdsData = async () => {
        setLoading(true);
        try {
            const response = await Calling.adminAdsTrackDashbord(startDate, endDate, selectedType);
            console.log('API Response:', response);
            if (response?.status) {
                setSummary(response?.summary || {});
                setAdsData(response?.adsEvents || []);
                setAllAdsData({
                    bannerAds: response?.bannerAds || [],
                    fullScreenAds: response?.fullScreenAds || [],
                    nativeAds: response?.nativeAds || [],
                    openAds: response?.openAds || [],
                    rewardAds: response?.rewardAds || [],
                    rewardFsAds: response?.rewardFsAds || [],
                });
            } else {
                toast.error(response?.message || "Failed to fetch Ads data");
            }
        } catch (error) {
            console.error("Error fetching Ads data:", error);
            toast.error("Server error while fetching Ads data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, selectedBtn, selectedType]);

    // Keep the picker drafts in sync whenever the committed range changes (quick buttons / apply)
    useEffect(() => {
        setDraftStart(startDate ? dayjs(startDate) : null);
        setDraftEnd(endDate ? dayjs(endDate) : null);
    }, [startDate, endDate]);

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();
        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istDate = new Date(date.getTime() + istOffset);
            return istDate.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectedBtn('today');
                break;
            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectedBtn('yesterday');
                break;
            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectedBtn('thisWeek');
                break;
            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectedBtn('thisMonth');
                break;
            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectedBtn('last6Months');
                break;
            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectedBtn('thisYear');
                break;
            case 'lastYear':
                const firstDayLastYearUTC = new Date(todayUTC.getFullYear() - 1, 0, 1);
                const lastDayLastYearUTC = new Date(todayUTC.getFullYear() - 1, 11, 31);
                start = toIST(firstDayLastYearUTC);
                end = toIST(lastDayLastYearUTC);
                setSelectedBtn('lastYear');
                break;
            default:
                start = '';
                end = '';
                setSelectedBtn('range');
        }

        setStartDate(start);
        setEndDate(end);
        // keep picker drafts aligned with the quick-range commit so the Apply button doesn't flash
        setDraftStart(start ? dayjs(start) : null);
        setDraftEnd(end ? dayjs(end) : null);
    };

    // Commit the picked draft range — this is the ONLY thing that applies a custom range
    const handleApplyDateRange = () => {
        if (!draftStart || !draftStart.isValid() || !draftEnd || !draftEnd.isValid()) {
            toast.error('Please select both start and end dates', { autoClose: 2000 });
            return;
        }
        const today = dayjs().endOf('day');
        if (draftStart.isAfter(today) || draftEnd.isAfter(today)) {
            toast.error('Future dates are not allowed', { autoClose: 2000 });
            return;
        }
        if (draftStart.isAfter(draftEnd, 'day')) {
            toast.error('Start date cannot be after end date', { autoClose: 2000 });
            return;
        }
        setStartDate(draftStart.format('YYYY-MM-DD'));
        setEndDate(draftEnd.format('YYYY-MM-DD'));
        setSelectedBtn('range');
    };

    const handleDeleteData = async () => {
        if (!startDate || !endDate) {
            toast.error("Please select a valid date range");
            return;
        }
        setDeleteLoading(true);
        try {
            const response = await Calling.adminAdsTrackDelete(startDate, endDate, selectedType);
            if (response?.status) {
                toast.success(response?.message || "Data deleted successfully");
                fetchAdsData();
                setShowDeleteModal(false);
            } else {
                toast.error(response?.message || "Failed to delete data");
            }
        } catch (error) {
            console.error("Error deleting data:", error);
            toast.error("Server error while deleting data");
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleImagePreview = (imageUrl, adName) => {
        setPreviewImage(imageUrl);
        setPreviewAdName(adName);
    };

    const handleViewEventDetails = (eventsByDate, adName) => {
        setSelectedEventsByDate(eventsByDate);
        setSelectedAdName(adName);
        setShowEventsModal(true);
    };

    const handleExportCSV = () => {
        try {
            const exportData = prepareExportData();
            if (exportData.length === 0) {
                toast.warning("No data available to export");
                return;
            }
            const headers = Object.keys(exportData[0]).join(',');
            const rows = exportData.map(row =>
                Object.values(row).map(value =>
                    typeof value === 'string' && (value.includes(',') || value.includes('"'))
                        ? `"${value.replace(/"/g, '""')}"`
                        : value
                ).join(',')
            );
            const csv = [headers, ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `AdsTrack_${startDate}_to_${endDate}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success("CSV file downloaded successfully");
        } catch (error) {
            console.error("Error exporting CSV:", error);
            toast.error("Failed to export CSV");
        }
    };

    const prepareExportData = () => {
        const tableData = getGroupedTableData();
        const exportData = [];

        tableData.forEach((item, index) => {
            if (item.eventsByDate && Object.keys(item.eventsByDate).length > 0) {
                Object.keys(item.eventsByDate).forEach((date) => {
                    const eventCounts = {
                        onAdLoaded: 0,
                        onAdFailedToLoad: 0,
                        onAdShowedFullScreenContent: 0,
                        onAdDismissedFullScreenContent: 0,
                        onAdFailedToShowFullScreenContent: 0,
                        onAdImpression: 0,
                        onAdClicked: 0,
                        onAdUnitNotFound: 0,
                        onRequestSend: 0
                    };
                    item.eventsByDate[date].forEach((event) => {
                        const eventType = event.a_event;
                        if (eventCounts.hasOwnProperty(eventType)) {
                            eventCounts[eventType] += event.a_count || 0;
                        }
                    });
                    exportData.push({
                        '#': index + 1,
                        'Ad Name': item.a_name || 'N/A',
                        'Ad ID': item.a_id || 'N/A',
                        'Description': item.a_description || 'N/A',
                        'Group': item.a_group || 'N/A',
                        'Group Title': item.a_group_title || 'N/A',
                        'Image URL': item.a_img || 'N/A',
                        'Event Date': date,
                        'onAdLoaded': eventCounts.onAdLoaded,
                        'onAdFailedToLoad': eventCounts.onAdFailedToLoad,
                        'onAdShowedFullScreenContent': eventCounts.onAdShowedFullScreenContent,
                        'onAdDismissedFullScreenContent': eventCounts.onAdDismissedFullScreenContent,
                        'onAdFailedToShowFullScreenContent': eventCounts.onAdFailedToShowFullScreenContent,
                        'onAdImpression': eventCounts.onAdImpression,
                        'onAdClicked': eventCounts.onAdClicked,
                        'onAdUnitNotFound': eventCounts.onAdUnitNotFound,
                        'onRequestSend': eventCounts.onRequestSend
                    });
                });
            }
        });
        return exportData;
    };

    const eventColors = {
        onRequestSend: '#06b6d4',
        onAdLoaded: '#3b82f6',
        onAdFailedToLoad: '#ef4444',
        onAdShowedFullScreenContent: '#10b981',
        onAdDismissedFullScreenContent: '#fb923c',
        onAdFailedToShowFullScreenContent: '#ec4899',
        onAdImpression: '#8b5cf6',
        onAdClicked: '#eab308',
        onAdUnitNotFound: '#6b7280',
    };

    const filteredAdsData = adsData;

    const getUniqueGroups = () => {
        const allData = Object.values(allAdsData).flat();
        const groupMap = new Map();
        allData.forEach(item => {
            if (item.a_group && item.a_group_title) {
                const key = `${item.a_group}|${item.a_group_title}`;
                if (!groupMap.has(key)) {
                    groupMap.set(key, { group: item.a_group, title: item.a_group_title });
                }
            }
        });
        return Array.from(groupMap.values()).sort((a, b) => a.group.localeCompare(b.group));
    };

    const parseGroupValue = (value) => {
        if (value === 'all') return { group: 'all', title: 'all' };
        const [group, title] = value.split('|');
        return { group, title };
    };

    const getFilteredAdsData = () => {
        let filtered = [...adsData];
        if (selectedGroup !== 'all') {
            const { group, title } = parseGroupValue(selectedGroup);
            const validRecordIds = new Set();
            Object.values(allAdsData).flat().forEach(config => {
                if (config.a_group === group && config.a_group_title === title) {
                    validRecordIds.add(config._id);
                }
            });
            filtered = filtered.filter(event => {
                if (validRecordIds.has(event.record_id)) return true;
                if (event.a_group === group && event.a_group_title === title) return true;
                return false;
            });
        }
        if (selectedAdId !== 'all') {
            filtered = filtered.filter(event => event.record_id === selectedAdId);
        }
        return filtered;
    };

    const uiFilteredAdsData = getFilteredAdsData();

    const eventsByDateChart = {};
    uiFilteredAdsData.forEach((event) => {
        const date = formatDateDisplay(event.createdAt);
        if (!eventsByDateChart[date]) {
            eventsByDateChart[date] = {};
        }
        if (!eventsByDateChart[date][event.a_event]) {
            eventsByDateChart[date][event.a_event] = 0;
        }
        eventsByDateChart[date][event.a_event] += event.a_count || 0;
    });

    const chartLabels = Object.keys(eventsByDateChart).sort((a, b) => {
        const parseDate = (dateStr) => {
            const months = { 'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5, 'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11 };
            const parts = dateStr.split(' ');
            const day = parseInt(parts[0]);
            const month = months[parts[1]];
            const year = parseInt(parts[2]);
            return new Date(Date.UTC(year, month, day));
        };
        return parseDate(a) - parseDate(b);
    });

    const allEventTypes = [...new Set(uiFilteredAdsData.map(e => e.a_event))];

    useEffect(() => {
        if (!chartRef.current) return;

        // Dispose existing instance first
        if (chartInstance.current) {
            chartInstance.current.dispose();
            chartInstance.current = null;
        }

        // Small delay for DOM readiness
        let resizeHandler = null;
        const timer = setTimeout(() => {
            if (!chartRef.current) return;

            chartInstance.current = echarts.init(chartRef.current);

            const series = allEventTypes.map((eventType) => {
                const color = eventColors[eventType] || '#9ca3af';
                return {
                    name: eventType,
                    type: 'line',
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 8,
                    lineStyle: { width: 2, color: color },
                    itemStyle: { color: color, borderColor: '#fff', borderWidth: 2 },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: color + '40' },
                            { offset: 1, color: color + '10' }
                        ])
                    },
                    emphasis: {
                        focus: 'series',
                        itemStyle: { borderWidth: 3, shadowBlur: 10, shadowColor: color + '80' }
                    },
                    data: chartLabels.map(date => eventsByDateChart[date]?.[eventType] || 0)
                };
            });

            const option = {
                tooltip: {
                    trigger: 'axis',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    borderColor: 'transparent',
                    borderRadius: 8,
                    padding: 12,
                    textStyle: { color: '#fff', fontSize: 13 },
                    axisPointer: { type: 'cross', crossStyle: { color: '#999' } },
                    formatter: function (params) {
                        let result = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>`;
                        params.forEach(param => {
                            result += `<div style="display: flex; align-items: center; justify-content: space-between; margin: 4px 0;">
                                <span style="display: flex; align-items: center;">
                                    <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${param.color}; margin-right: 8px;"></span>
                                    ${param.seriesName}
                                </span>
                                <span style="font-weight: bold; margin-left: 20px;">${param.value} events</span>
                            </div>`;
                        });
                        return result;
                    }
                },
                legend: {
                    type: 'scroll',
                    top: 0,
                    left: 'center',
                    icon: 'circle',
                    itemWidth: 10,
                    itemHeight: 10,
                    itemGap: 15,
                    textStyle: { fontSize: 11, fontWeight: 500, color: '#374151' },
                    pageIconColor: '#3b82f6',
                    pageIconInactiveColor: '#9ca3af',
                    pageTextStyle: { color: '#374151' }
                },
                grid: { left: '3%', right: '4%', bottom: '3%', top: '15%', containLabel: true },
                xAxis: {
                    type: 'category',
                    boundaryGap: false,
                    data: chartLabels,
                    axisLine: { lineStyle: { color: '#e5e7eb' } },
                    axisLabel: { color: '#6b7280', fontSize: 11, rotate: chartLabels.length > 10 ? 45 : 0 },
                    axisTick: { show: false }
                },
                yAxis: {
                    type: 'value',
                    minInterval: 1,
                    axisLine: { show: false },
                    axisLabel: { color: '#6b7280', fontSize: 11 },
                    splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } }
                },
                series: series
            };

            chartInstance.current.setOption(option, true);

            resizeHandler = () => {
                chartInstance.current?.resize();
            };
            window.addEventListener('resize', resizeHandler);
        }, 0);

        return () => {
            clearTimeout(timer);
            if (resizeHandler) {
                window.removeEventListener('resize', resizeHandler);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uiFilteredAdsData, chartLabels, allEventTypes, eventsByDateChart]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (chartInstance.current) {
                try {
                    chartInstance.current.dispose();
                } catch (e) {
                    console.warn('Chart dispose error:', e);
                }
                chartInstance.current = null;
            }
        };
    }, []);


    const getEventAnalytics = () => {
        const analytics = {
            totalEvents: uiFilteredAdsData.length,
            totalCount: uiFilteredAdsData.reduce((sum, event) => sum + (event.a_count || 0), 0),
            onAdLoaded: 0,
            onAdFailedToLoad: 0,
            onAdShowedFullScreenContent: 0,
            onAdDismissedFullScreenContent: 0,
            onAdFailedToShowFullScreenContent: 0,
            onAdImpression: 0,
            onAdClicked: 0,
            onAdUnitNotFound: 0,
            onRequestSend: 0,
        };
        uiFilteredAdsData.forEach(event => {
            const count = event.a_count || 0;
            const eventType = event.a_event;
            if (analytics[eventType] !== undefined) {
                analytics[eventType] += count;
            }
        });
        return analytics;
    };

    const eventAnalytics = getEventAnalytics();

    const summaryCards = [
        { key: 'bannerAds', label: 'Banner Ads', color: 'from-blue-500 to-blue-600', bgColor: 'bg-blue-50', textColor: 'text-blue-600' },
        { key: 'fullScreenAds', label: 'Full Screen', color: 'from-purple-500 to-purple-600', bgColor: 'bg-purple-50', textColor: 'text-purple-600' },
        { key: 'nativeAds', label: 'Native Ads', color: 'from-green-500 to-green-600', bgColor: 'bg-green-50', textColor: 'text-green-600' },
        { key: 'openAds', label: 'Open Ads', color: 'from-orange-500 to-orange-600', bgColor: 'bg-orange-50', textColor: 'text-orange-600' },
        { key: 'rewardAds', label: 'Reward Ads', color: 'from-pink-500 to-pink-600', bgColor: 'bg-pink-50', textColor: 'text-pink-600' },
        { key: 'rewardFsAds', label: 'RewardFs Ads', color: 'from-indigo-500 to-indigo-600', bgColor: 'bg-indigo-50', textColor: 'text-indigo-600' },
    ];

    const eventCards = [
        { label: 'Request Send', value: eventAnalytics.onRequestSend, subValue: 'Ad Requests', icon: TrendingUp, color: 'from-cyan-500 to-cyan-600', bgColor: 'bg-cyan-50', textColor: 'text-cyan-600', eventType: 'onRequestSend' },
        { label: 'Ad Loaded', value: eventAnalytics.onAdLoaded, subValue: 'Success', icon: CheckCircle2, color: 'from-blue-500 to-blue-600', bgColor: 'bg-blue-50', textColor: 'text-blue-600', eventType: 'onAdLoaded' },
        { label: 'Load Failed', value: eventAnalytics.onAdFailedToLoad, subValue: 'Load Errors', icon: XCircle, color: 'from-red-500 to-red-600', bgColor: 'bg-red-50', textColor: 'text-red-600', eventType: 'onAdFailedToLoad' },
        { label: 'Showed Full Screen', value: eventAnalytics.onAdShowedFullScreenContent, subValue: 'Displayed', icon: Eye, color: 'from-green-500 to-green-600', bgColor: 'bg-green-50', textColor: 'text-green-600', eventType: 'onAdShowedFullScreenContent' },
        { label: 'Dismissed', value: eventAnalytics.onAdDismissedFullScreenContent, subValue: 'Closed', icon: Activity, color: 'from-orange-500 to-orange-600', bgColor: 'bg-orange-50', textColor: 'text-orange-600', eventType: 'onAdDismissedFullScreenContent' },
        { label: 'Show Failed', value: eventAnalytics.onAdFailedToShowFullScreenContent, subValue: 'Display Errors', icon: AlertCircle, color: 'from-pink-500 to-pink-600', bgColor: 'bg-pink-50', textColor: 'text-pink-600', eventType: 'onAdFailedToShowFullScreenContent' },
        { label: 'Impressions', value: eventAnalytics.onAdImpression, subValue: 'Views', icon: Eye, color: 'from-purple-500 to-purple-600', bgColor: 'bg-purple-50', textColor: 'text-purple-600', eventType: 'onAdImpression' },
        { label: 'Clicks', value: eventAnalytics.onAdClicked, subValue: 'Interactions', icon: MousePointerClick, color: 'from-yellow-500 to-yellow-600', bgColor: 'bg-yellow-50', textColor: 'text-yellow-600', eventType: 'onAdClicked' },
        { label: 'Unit Not Found', value: eventAnalytics.onAdUnitNotFound, subValue: 'Configuration Errors', icon: AlertCircle, color: 'from-gray-500 to-gray-600', bgColor: 'bg-gray-50', textColor: 'text-gray-600', eventType: 'onAdUnitNotFound' },
    ];

    const getUniqueAdIds = () => {
        let allData = Object.values(allAdsData).flat();

        // Filter by selected group if not 'all'
        if (selectedGroup !== 'all') {
            const { group, title } = parseGroupValue(selectedGroup);
            allData = allData.filter(item => item.a_group === group && item.a_group_title === title);
        }

        const uniqueAds = allData.map(item => ({
            _id: item._id,
            a_name: item.a_name,
            a_id: item.a_id,
            a_description: item.a_description,
            a_group: item.a_group,
            a_group_title: item.a_group_title
        }));
        return uniqueAds;
    };

    const getGroupedTableData = () => {
        let filteredData = [];
        if (selectedType === 'all') {
            filteredData = Object.values(allAdsData).flat();
        } else {
            filteredData = allAdsData[selectedType] || [];
        }

        if (selectedGroup !== 'all') {
            const { group, title } = parseGroupValue(selectedGroup);
            filteredData = filteredData.filter(item => item.a_group === group && item.a_group_title === title);
        }

        if (selectedAdId !== 'all') {
            filteredData = filteredData.filter(item => item._id === selectedAdId);
        }

        if (searchTerm) {
            filteredData = filteredData.filter(item =>
                item.a_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.a_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.a_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.a_group_title?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        const grouped = {};
        filteredData.forEach(item => {
            const key = item._id;
            if (!grouped[key]) {
                grouped[key] = { ...item, eventsByDate: {} };
            }
        });

        uiFilteredAdsData.forEach(event => {
            const matchingKey = Object.keys(grouped).find(key => grouped[key]._id === event.record_id);
            if (matchingKey) {
                const eventDate = formatDateOnly(event.createdAt);
                if (!grouped[matchingKey].eventsByDate[eventDate]) {
                    grouped[matchingKey].eventsByDate[eventDate] = [];
                }
                grouped[matchingKey].eventsByDate[eventDate].push(event);
            }
        });

        return Object.values(grouped);
    };

    const handleEventCardClick = (eventType) => {
        setSelectedEventType(eventType);
        setShowEventDetailModal(true);
    };

    const tableData = getGroupedTableData();
    const uniqueAdIds = getUniqueAdIds();
    const uniqueGroups = getUniqueGroups();

    // Estimated records affected by a delete (only date-range + type apply)
    const getAffectedRecordsCount = () => {
        let allData = [];
        if (selectedType === 'all') {
            allData = Object.values(allAdsData).flat();
        } else {
            allData = allAdsData[selectedType] || [];
        }
        return allData.length;
    };

    const TYPE_FILTERS = [
        { value: 'all', label: 'All' },
        { value: 'bannerAds', label: 'Banner' },
        { value: 'fullScreenAds', label: 'Full Screen' },
        { value: 'nativeAds', label: 'Native' },
        { value: 'openAds', label: 'Open' },
        { value: 'rewardAds', label: 'Reward' },
        { value: 'rewardFsAds', label: 'RewardFs' },
    ];

    // Full-page skeleton only on the INITIAL load (no data yet) so refreshes never
    // swap the whole tree out from under an exiting AnimatePresence modal.
    const initialLoading = loading && adsData.length === 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full min-h-screen text-main-font"
        >
            {/* Page Header */}
            <div className="mb-6">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-main-blue to-emerald-600 flex items-center justify-center text-main-white shadow-lg shadow-main-blue/30">
                        <Activity size={26} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font tracking-tight">Ads Analytics Dashboard</h1>
                        <p className="text-sm text-main-subfont mt-1">Track and analyze your ad performance in real-time</p>
                    </div>
                </div>
            </div>

            {/* Date Range Selection Card */}
            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden mb-6">
                <div className="p-5">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 bg-main-blue/10 rounded-xl flex items-center justify-center">
                            <CalendarDays className="text-main-blue" size={20} />
                        </div>
                        <div>
                            <h2 className="text-base font-semibold text-main-font">Date Range</h2>
                            <p className="text-xs text-main-subfont">Select a time period to analyze</p>
                        </div>
                    </div>

                    <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-5">
                        {/* Quick Range — modern segmented control */}
                        <div>
                            <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <CalendarDays size={13} /> Quick Range
                            </p>
                            <div className="inline-flex flex-wrap items-center gap-1 bg-main-bg border border-main-border rounded-xl p-1">
                                {QUICK_RANGES.map((r) => {
                                    const active = selectedBtn === r.key;
                                    return (
                                        <button
                                            key={r.key}
                                            type="button"
                                            onClick={() => handleDateRangeFilter(r.key)}
                                            className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${active
                                                ? 'bg-main-blue text-main-white shadow-sm shadow-main-blue/30'
                                                : 'text-main-subfont hover:text-main-font hover:bg-main-white'
                                                }`}
                                        >
                                            {r.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Custom Range — themed MUI date pickers + Apply (applies only on click) */}
                        <div>
                            <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <CalendarRange size={13} /> Custom Range
                            </p>
                            <ThemeProvider theme={pickerTheme}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 bg-main-bg border border-main-border rounded-2xl p-2.5 shadow-sm">
                                        <DatePicker
                                            label="From"
                                            value={draftStart}
                                            onChange={(v) => setDraftStart(v)}
                                            format="DD MMM YYYY"
                                            disableFuture
                                            maxDate={draftEnd || undefined}
                                            slots={pickerSlots}
                                            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                        />
                                        <span className="hidden sm:flex items-center justify-center text-main-subfont">
                                            <ArrowRight size={16} />
                                        </span>
                                        <DatePicker
                                            label="To"
                                            value={draftEnd}
                                            onChange={(v) => setDraftEnd(v)}
                                            format="DD MMM YYYY"
                                            disableFuture
                                            minDate={draftStart || undefined}
                                            slots={pickerSlots}
                                            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleApplyDateRange}
                                            sx={{ ...applyBtnSx, display: ((draftStart && draftStart.isValid() ? draftStart.format('YYYY-MM-DD') : '') !== (startDate || '') || (draftEnd && draftEnd.isValid() ? draftEnd.format('YYYY-MM-DD') : '') !== (endDate || '')) ? 'inline-flex' : 'none' }}
                                            startIcon={<Check size={16} />}
                                        >
                                            Apply
                                        </Button>
                                    </div>
                                </LocalizationProvider>
                            </ThemeProvider>
                        </div>
                    </div>

                    {/* Applied-range indicator */}
                    {selectedBtn === 'range' && startDate && endDate && (
                        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-main-th border border-main-blue/20 rounded-lg">
                            <CalendarRange size={14} className="text-main-blue" />
                            <span className="text-xs text-main-subfont">
                                Showing <span className="font-semibold text-main-font">{startDate}</span> → <span className="font-semibold text-main-font">{endDate}</span>
                            </span>
                        </div>
                    )}
                </div>

                {/* Data Type Filter */}
                <div className="px-5 py-4 border-t border-main-border bg-main-th/50">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-main-font mr-2">Filter by Type:</span>
                        {TYPE_FILTERS.map((type) => (
                            <button
                                key={type.value}
                                onClick={() => setSelectedType(type.value)}
                                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${selectedType === type.value
                                    ? 'bg-main-blue text-main-white shadow-sm shadow-main-blue/30'
                                    : 'bg-main-white text-main-subfont border border-main-border hover:border-main-blue/50 hover:text-main-blue'
                                    }`}
                            >
                                {type.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Advanced Filters Card */}
            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden mb-6">
                <div className="p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-main-exBlue/10 rounded-xl flex items-center justify-center">
                                <Filter className="text-main-exBlue" size={20} />
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-main-font">Advanced Filters</h3>
                                <p className="text-xs text-main-subfont">Refine by group, ad and search</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={fetchAdsData}
                                disabled={loading}
                                className="px-5 py-2.5 bg-main-white text-main-font rounded-xl font-semibold hover:bg-main-th transition-all flex items-center gap-2 text-sm border border-main-border disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Refresh data"
                            >
                                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                            </button>
                            <button
                                onClick={handleExportCSV}
                                className="px-5 py-2.5 bg-main-blue text-main-white rounded-xl font-semibold hover:bg-emerald-600 transition-all flex items-center gap-2 text-sm shadow-lg shadow-main-blue/25"
                                title="Export CSV"
                            >
                                <Download size={18} />
                            </button>
                            <button
                                onClick={() => setShowDeleteModal(true)}
                                className="px-5 py-2.5 bg-main-red text-main-white rounded-xl font-semibold hover:bg-red-600 transition-all flex items-center gap-2 text-sm shadow-lg shadow-main-red/25"
                                title="Delete data"
                            >
                                <XCircle size={18} />
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-main-subfont mb-2">Filter by Group</label>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={selectedGroup}
                                    onChange={(e) => {
                                        setSelectedGroup(e.target.value);
                                        setSelectedAdId('all');
                                    }}
                                    sx={modalSelectSx}
                                >
                                    <MenuItem value="all">All Groups</MenuItem>
                                    {uniqueGroups.map(item => (
                                        <MenuItem key={`${item.group}|${item.title}`} value={`${item.group}|${item.title}`}>
                                            {item.group} ({item.title})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-main-subfont mb-2">Filter by Ad ID</label>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={selectedAdId}
                                    onChange={(e) => setSelectedAdId(e.target.value)}
                                    sx={modalSelectSx}
                                >
                                    <MenuItem value="all">All Ads</MenuItem>
                                    {uniqueAdIds.map(ad => (
                                        <MenuItem key={ad._id} value={ad._id}>
                                            {ad.a_name || 'Unknown'} - {ad.a_description ? ad.a_description.substring(0, 30) : 'No description'}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-main-subfont mb-2">Search</label>
                            <TextField
                                fullWidth
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search ad name, ID..."
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' } }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search size={18} className="text-main-subfont" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </div>
                        <div className="flex items-end">
                            {(selectedGroup !== 'all' || selectedAdId !== 'all' || searchTerm) && (
                                <button
                                    onClick={() => { setSelectedGroup('all'); setSelectedAdId('all'); setSearchTerm(''); }}
                                    className="text-sm text-main-blue hover:text-emerald-600 font-medium flex items-center gap-1"
                                >
                                    <XCircle size={16} /> Clear all filters
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Ad Configuration Summary */}
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-main-blue/10 rounded-xl flex items-center justify-center">
                        <Award className="text-main-blue" size={20} />
                    </div>
                    <div>
                        <h3 className="text-base font-semibold text-main-font">Ad Configuration Summary</h3>
                        <p className="text-xs text-main-subfont">Configured ad records by type</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {initialLoading
                        ? summaryCards.map((card) => (
                            <div key={card.key} className="bg-main-white rounded-2xl border border-main-border overflow-hidden">
                                <Skeleton variant="rectangular" height={6} />
                                <div className="p-4">
                                    <Skeleton variant="text" width={70} height={16} />
                                    <Skeleton variant="text" width={48} height={32} />
                                </div>
                            </div>
                        ))
                        : summaryCards.map((card) => {
                            const cardData = summary[card.key] || { total: 0, totalCount: 0 };
                            return (
                                <div key={card.key} className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden hover:shadow-md transition-all duration-300">
                                    <div className={`h-1.5 bg-gradient-to-r ${card.color}`}></div>
                                    <div className="p-4">
                                        <p className="text-xs font-medium text-main-subfont uppercase tracking-wide">{card.label}</p>
                                        <p className="text-xl font-bold text-main-font mt-1">{cardData.total.toLocaleString()}</p>
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>

            {/* Event Analytics */}
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-main-exBlue/10 rounded-xl flex items-center justify-center">
                        <TrendingUp className="text-main-exBlue" size={20} />
                    </div>
                    <div>
                        <h3 className="text-base font-semibold text-main-font">Event Analytics</h3>
                        <p className="text-xs text-main-subfont">Click a card to drill into event details</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {initialLoading
                        ? Array.from({ length: 9 }).map((_, i) => (
                            <div key={i} className="bg-main-white rounded-2xl border border-main-border overflow-hidden">
                                <Skeleton variant="rectangular" height={6} />
                                <div className="p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <Skeleton variant="text" width={70} height={16} />
                                        <Skeleton variant="rounded" width={36} height={36} />
                                    </div>
                                    <Skeleton variant="text" width={56} height={32} />
                                </div>
                            </div>
                        ))
                        : eventCards.map((card, index) => {
                            const Icon = card.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.04 }}
                                    onClick={() => handleEventCardClick(card.eventType)}
                                    className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer group"
                                >
                                    <div className={`h-1.5 bg-gradient-to-r ${card.color}`}></div>
                                    <div className="p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-xs font-medium text-main-subfont uppercase tracking-wide">{card.label}</p>
                                            <div className={`w-9 h-9 ${card.bgColor} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                                <Icon className={card.textColor} size={18} />
                                            </div>
                                        </div>
                                        <p className="text-2xl font-bold text-main-font">{card.value.toLocaleString()}</p>
                                        <div className="pt-2 mt-2 border-t border-main-border flex items-center justify-between">
                                            <span className="text-xs text-main-subfont">{card.subValue}</span>
                                            <Eye size={16} className="text-main-subfont group-hover:text-main-blue transition-colors" />
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                </div>
            </div>

            {/* Chart Section */}
            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden mb-6">
                <div className="p-5 border-b border-main-border">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-main-yellow/10 rounded-xl flex items-center justify-center">
                                <Activity className="text-main-yellow" size={20} />
                            </div>
                            <div>
                                <h2 className="text-base font-semibold text-main-font">Ad Events Timeline</h2>
                                <p className="text-xs text-main-subfont mt-0.5">Track event trends over time</p>
                            </div>
                        </div>
                        <span className="text-xs font-semibold text-main-subfont bg-main-bg px-3 py-1.5 rounded-lg border border-main-border">
                            {filteredAdsData.length} events
                        </span>
                    </div>
                </div>
                <div className="p-5 relative" style={{ minHeight: 400 }}>
                    {/* ECharts node stays ALWAYS mounted with a stable identity (no dynamic key,
                        no conditional unmount) so the async init never lands on a detached node
                        and React never removes an ECharts-owned node → no removeChild crash. */}
                    <div
                        ref={chartRef}
                        style={{ width: '100%', height: 400, opacity: filteredAdsData.length > 0 ? 1 : 0, transition: 'opacity .2s' }}
                    />
                    {filteredAdsData.length === 0 && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-main-white text-main-subfont">
                            <div className="w-20 h-20 bg-main-bg rounded-full flex items-center justify-center mb-5">
                                <Activity size={36} />
                            </div>
                            <p className="text-lg font-semibold text-main-font">No ad events available</p>
                            <p className="text-sm mt-1">Try selecting a different date range or adjusting filters</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Table Section */}
            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                <div className="p-5 border-b border-main-border">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-main-exBlue/10 rounded-xl flex items-center justify-center">
                                <Filter className="text-main-exBlue" size={20} />
                            </div>
                            <div>
                                <h2 className="text-base font-semibold text-main-font">Ad Events by Configuration</h2>
                                <p className="text-xs text-main-subfont mt-0.5">{tableData.length} configurations found</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead>
                            <tr className="bg-main-th">
                                <th className="px-4 py-4 text-left text-xs font-bold text-main-font uppercase tracking-wider">#</th>
                                <th className="px-4 py-4 text-left text-xs font-bold text-main-font uppercase tracking-wider">Ad Details</th>
                                <th className="px-4 py-4 text-left text-xs font-bold text-main-font uppercase tracking-wider">Ad ID</th>
                                <th className="px-4 py-4 text-center text-xs font-bold text-main-font uppercase tracking-wider">Group</th>
                                <th className="px-4 py-4 text-center text-xs font-bold text-main-font uppercase tracking-wider">Group Title</th>
                                <th className="px-4 py-4 text-center text-xs font-bold text-main-font uppercase tracking-wider">Image</th>
                                <th className="px-4 py-4 text-center text-xs font-bold text-main-font uppercase tracking-wider">Events</th>
                            </tr>
                        </thead>
                        {initialLoading ? (
                            <tbody>
                                <tr>
                                    <td colSpan={7} className="p-0">
                                        <TableSkeleton rows={8} cols={7} />
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody className="divide-y divide-main-border">
                                {tableData.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center">
                                                <div className="w-16 h-16 bg-main-bg rounded-full flex items-center justify-center mb-4">
                                                    <Activity size={28} className="text-main-subfont" />
                                                </div>
                                                <p className="text-main-font font-semibold">No records found</p>
                                                <p className="text-sm text-main-subfont mt-1">Try adjusting your filters</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    tableData.map((item, i) => (
                                        <tr key={item._id} className="hover:bg-main-thHover transition-colors">
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <span className="text-sm font-bold text-main-font bg-main-bg w-8 h-8 rounded-lg flex items-center justify-center">
                                                    {i + 1}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-main-font">{item.a_name}</span>
                                                    {item.a_description && (
                                                        <span className="text-xs text-main-subfont mt-1">
                                                            {item.a_description.length > 50 ? `${item.a_description.substring(0, 50)}...` : item.a_description}
                                                        </span>
                                                    )}
                                                    <span className="text-xs text-main-subfont mt-1">Created: {formatDateOnly(item.createdAt)}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className="text-xs font-mono text-main-subfont bg-main-bg px-2 py-1 rounded-lg block max-w-xs truncate" title={item.a_id}>
                                                    {item.a_id || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                <span className={`px-3 py-1 text-xs font-bold rounded-full ${item.a_group === 'A' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
                                                    Group {item.a_group}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                <span className="px-3 py-1 text-xs font-medium bg-main-th text-main-exBlue rounded-full">
                                                    {item.a_group_title || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                {item.a_img ? (
                                                    <button
                                                        onClick={() => handleImagePreview(item.a_img, item.a_name)}
                                                        className="inline-flex items-center justify-center w-9 h-9 bg-main-exBlue/10 hover:bg-main-exBlue/20 rounded-xl transition-colors group"
                                                        title="Click to view image"
                                                    >
                                                        <Eye size={18} className="text-main-exBlue group-hover:scale-110 transition-transform" />
                                                    </button>
                                                ) : (
                                                    <span className="text-xs text-main-subfont italic">No image</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                {item.eventsByDate && Object.keys(item.eventsByDate).length > 0 ? (
                                                    <button
                                                        onClick={() => handleViewEventDetails(item.eventsByDate, item.a_name)}
                                                        className="inline-flex items-center justify-center w-9 h-9 bg-main-blue hover:bg-emerald-600 rounded-xl transition-all shadow-md hover:shadow-lg group"
                                                        title="View analytics"
                                                    >
                                                        <Eye size={18} className="text-white group-hover:scale-110 transition-transform" />
                                                    </button>
                                                ) : (
                                                    <span className="text-xs text-main-subfont italic">No events</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        )}
                    </table>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {showDeleteModal && (
                    <motion.div
                        key="ads-delete-modal"
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="bg-main-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                            initial={{ scale: 0.92, y: 16, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            exit={{ scale: 0.92, y: 16, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            {/* Modal Header */}
                            <div className="bg-main-red/10 p-5">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 bg-main-red/20 rounded-2xl flex items-center justify-center">
                                        <AlertCircle className="text-main-red" size={30} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-main-font">Delete Ad Track Data</h3>
                                        <p className="text-sm text-main-subfont mt-0.5">This action cannot be undone</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-5">
                                <div className="space-y-4">
                                    <p className="text-main-subfont text-sm">
                                        You are about to delete ad tracking data with the following filters:
                                    </p>

                                    <div className="bg-main-bg rounded-xl p-4 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-main-subfont">Date Range</span>
                                            <span className="text-sm font-semibold text-main-font">{startDate} → {endDate}</span>
                                        </div>
                                        <div className="h-px bg-main-border"></div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-main-subfont">Ad Type</span>
                                            <span className="text-sm font-semibold text-main-font">{selectedType === 'all' ? 'All Types' : selectedType}</span>
                                        </div>
                                        <div className="h-px bg-main-border"></div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-main-subfont">Estimated Records</span>
                                            <span className="text-sm font-bold text-main-red">~{getAffectedRecordsCount()} configurations</span>
                                        </div>
                                    </div>

                                    <div className="bg-main-exBlue/10 border border-main-exBlue/30 rounded-xl p-4">
                                        <p className="text-sm text-main-font font-medium flex items-start gap-2">
                                            <AlertCircle size={16} className="text-main-exBlue mt-0.5 flex-shrink-0" />
                                            <span>Group and Ad ID filters will not affect deletion. Only Date Range and Type will be used.</span>
                                        </p>
                                    </div>

                                    <div className="bg-main-yellow/10 border border-main-yellow/30 rounded-xl p-4">
                                        <p className="text-sm text-main-font font-medium flex items-start gap-2">
                                            <span className="text-main-yellow text-lg">⚠</span>
                                            <span>All data matching the date range and type will be permanently removed from the system.</span>
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-3 mt-6">
                                    <button
                                        onClick={() => setShowDeleteModal(false)}
                                        disabled={deleteLoading}
                                        className="flex-1 px-5 py-3 bg-main-bg text-main-font rounded-xl font-semibold hover:bg-gray-100 transition-all disabled:opacity-50 border border-main-border"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDeleteData}
                                        disabled={deleteLoading}
                                        className="flex-1 px-5 py-3 bg-main-red text-main-white rounded-xl font-semibold hover:bg-red-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-main-red/25"
                                    >
                                        {deleteLoading ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>Deleting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <XCircle size={18} />
                                                <span>Delete Data</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Image Preview Modal */}
            <AnimatePresence>
                {previewImage && (
                    <ImagePreviewModal
                        imageUrl={previewImage}
                        adName={previewAdName}
                        onClose={() => { setPreviewImage(null); setPreviewAdName(''); }}
                    />
                )}
            </AnimatePresence>

            {/* Events Detail Modal */}
            <AnimatePresence>
                {showEventsModal && (
                    <EventsDetailModal
                        eventsByDate={selectedEventsByDate}
                        adName={selectedAdName}
                        onClose={() => { setShowEventsModal(false); setSelectedEventsByDate({}); setSelectedAdName(''); }}
                    />
                )}
            </AnimatePresence>

            {/* Event Type Detail Modal */}
            <AnimatePresence>
                {showEventDetailModal && (
                    <EventDetailModal
                        eventType={selectedEventType}
                        eventData={uiFilteredAdsData}
                        allAdsData={allAdsData}
                        onClose={() => { setShowEventDetailModal(false); setSelectedEventType(''); }}
                    />
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default AdsTrack;
